import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createSelector } from 'reselect';

import { ResidenceExplorerResponse, ResidenceExplorerSetup } from 'features/residenceExplorer/types';
import { SearchService } from 'features/search/services/SearchService';
import { useMetaStore } from 'features/meta/store/useMetaStore';
import { ObjectStatus, SortBy } from 'features/search/filters/enums';
import {
    FILTERS_MAX_AREA,
    FILTERS_MAX_PRICE,
    FILTERS_MAX_ROOMS,
    FILTERS_MIN_AREA,
    FILTERS_MIN_PRICE,
    FILTERS_MIN_ROOMS
} from 'features/search/filters/constants';
import { areaFilter, areaFilterNoBra, priceFilter, roomFilter } from 'features/search/filters/helpers';
import { SITE } from 'features/ui/enums';
import { getCurrentSite } from 'features/services/utils/env-helper';
import { SearchResult } from 'features/search/types';
import { logError } from 'features/services/utils/utilities';
import { User } from 'features/authentication/models/User';
import { collectApartmentsByTopLevel } from 'features/residenceExplorer/helpers';
import { ResidenceExplorerService } from 'features/residenceExplorer/services/service';

export interface ResidenceExplorerState {
    explorer: ResidenceExplorerResponse | null;
    objects: SearchResult[];
    currentViewName: string;
    settings: ResidenceExplorerSetup | null;
    hideObjects: boolean;
    selectedObjectId: string;
    showSunStudy: boolean;
    sunStudyStarted: boolean;
    sunStudyFrame: number;
    sunStudyPeriod: number;
    loading: boolean;
    sort: SortBy;
    filters: {
        minRooms: number;
        maxRooms: number;
        minPrice: number;
        maxPrice: number;
        minArea: number;
        maxArea: number;
    };
}

interface ResidenceExplorerActions {
    // Setup actions
    setupResidenceExplorer: (settings: ResidenceExplorerSetup, user: User | null | undefined) => Promise<void>;
    getResidenceExplorerData: (stageId: string) => Promise<void>;
    getResidenceExplorerObjects: (settings: ResidenceExplorerSetup, user: User | null | undefined) => Promise<void>;

    // State setters
    setExplorer: (explorer: ResidenceExplorerResponse) => void;
    setExplorerObjects: (objects: SearchResult[]) => void;
    setCurrentViewName: (viewName: string) => void;
    setSettings: (settings: ResidenceExplorerSetup) => void;
    setLoading: (loading: boolean) => void;
    setHideObjects: (hide: boolean) => void;
    setSelectedObjectId: (objectId: string) => void;
    setShowSunStudy: (show: boolean) => void;
    setSunStudyStarted: (started: boolean) => void;
    setSunStudyFrame: (frame: number) => void;
    setSunStudyPeriod: (period: number) => void;
    setSort: (sort: SortBy) => void;

    // Filter actions
    setRoomsFilter: (minRooms: number, maxRooms: number) => void;
    setPriceFilter: (minPrice: number, maxPrice: number) => void;
    setAreaFilter: (minArea: number, maxArea: number) => void;
    resetFilters: () => void;
    resetExplorer: () => void;
}

const initialState: ResidenceExplorerState = {
    explorer: null,
    objects: [],
    currentViewName: '',
    settings: null,
    hideObjects: false,
    selectedObjectId: '',
    showSunStudy: false,
    sunStudyStarted: false,
    sunStudyFrame: 0,
    sunStudyPeriod: 0,
    loading: false,
    sort: SortBy.ACCESS_ASC,
    filters: {
        minRooms: FILTERS_MIN_ROOMS,
        maxRooms: FILTERS_MAX_ROOMS,
        minPrice: FILTERS_MIN_PRICE,
        maxPrice: FILTERS_MAX_PRICE,
        minArea: FILTERS_MIN_AREA,
        maxArea: FILTERS_MAX_AREA
    }
};

export const useResidenceExplorerStore = create<ResidenceExplorerState & ResidenceExplorerActions>()(
    immer((set, get) => ({
        ...initialState,

        setupResidenceExplorer: async (settings, user) => {
            try {
                set({ loading: true, settings });

                if (settings.hasExplorer) {
                    await Promise.all([
                        get().getResidenceExplorerData(settings.stageId),
                        get().getResidenceExplorerObjects(settings, user),
                    ]);
                } else {
                    await get().getResidenceExplorerObjects(settings, user);
                }
            } catch (error) {
                logError(error, 'Could not setup residenceExplorer.');
            } finally {
                set({ loading: false });
            }
        },

        getResidenceExplorerData: async (stageId) => {
            try {
                const data = await ResidenceExplorerService.getData(stageId);

                set((state) => {
                    state.explorer = data;

                    if (state.settings?.currentObjectId) {
                        let apartment = data.apts[state.settings.currentObjectId];

                        if (!apartment) {
                            const found = Object.values(data.apts).find(
                                (apt) => state.settings && apt.name === state.settings.currentObjectId
                            );
                            if (found) apartment = found;
                        }

                        if (apartment) {
                            state.currentViewName = apartment.viewId;
                        } else {
                            state.currentViewName = data.settings.firstView;
                        }
                    } else {
                        state.currentViewName = data.settings.firstView;
                    }

                    if (data.sunstudy && data.sunstudy[data.settings.firstView]) {
                        state.sunStudyFrame = data.sunstudy[data.settings.firstView].data[0].startFrame;
                    }
                });
            } catch (error) {
                logError(error, 'Could not fetch residenceExplorerData.');
            }
        },

        getResidenceExplorerObjects: async (settings, user) => {
            try {
                const isInEditMode = useMetaStore.getState().isInEditMode;

                const objects = await SearchService.getStageObjects({
                    siteId: settings.siteId,
                    stageIds: settings.stageIds,
                    isEditMode: isInEditMode,
                    user,
                });

                set({ objects });
            } catch (error) {
                logError(error, 'Could not fetch residenceExplorerObjects.');
            }
        },

        setExplorer: (explorer) => set({ explorer }),

        setExplorerObjects: (objects) => set({ objects }),

        setCurrentViewName: (viewName) => set((state) => {
            // Turn off sunstudy if the current view does not have sunstudy
            if (!(state.showSunStudy && state.explorer?.sunstudy?.[viewName])) {
                state.showSunStudy = false;
            }
            state.currentViewName = viewName;
        }),

        setSettings: (settings) => set({ settings }),

        setLoading: (loading) => set({ loading }),

        setHideObjects: (hide) => set({ hideObjects: hide }),

        setSelectedObjectId: (objectId) => set({ selectedObjectId: objectId }),

        setShowSunStudy: (show) => set({ showSunStudy: show }),

        setSunStudyStarted: (started) => set({ sunStudyStarted: started }),

        setSunStudyFrame: (frame) => set({ sunStudyFrame: frame }),

        setSunStudyPeriod: (period) => set({ sunStudyPeriod: period }),

        setSort: (sort) => set({ sort }),

        setRoomsFilter: (minRooms, maxRooms) => set((state) => {
            state.filters.minRooms = minRooms;
            state.filters.maxRooms = maxRooms;
        }),

        setPriceFilter: (minPrice, maxPrice) => set((state) => {
            state.filters.minPrice = minPrice;
            state.filters.maxPrice = maxPrice;
        }),

        setAreaFilter: (minArea, maxArea) => set((state) => {
            state.filters.minArea = minArea;
            state.filters.maxArea = maxArea;
        }),

        resetFilters: () => set((state) => {
            state.filters = {
                minRooms: FILTERS_MIN_ROOMS,
                maxRooms: FILTERS_MAX_ROOMS,
                minPrice: FILTERS_MIN_PRICE,
                maxPrice: FILTERS_MAX_PRICE,
                minArea: FILTERS_MIN_AREA,
                maxArea: FILTERS_MAX_AREA
            };
        }),

        resetExplorer: () => set(initialState),
    }))
);

// Type for selectors
type StoreState = ResidenceExplorerState & ResidenceExplorerActions;

// Basic selectors
export const selectExplorer = (state: StoreState) => state.explorer;
export const selectResidenceExplorerSettings = (state: StoreState) => state.explorer?.settings;
export const selectExplorerObjects = (state: StoreState) => state.objects;
export const selectCurrentViewName = (state: StoreState) => state.currentViewName;
export const selectSetup = (state: StoreState) => state.settings;
export const selectHideObjects = (state: StoreState) => state.hideObjects;
export const selectSelectedObjectId = (state: StoreState) => state.selectedObjectId;
export const selectFilters = (state: StoreState) => state.filters;
export const selectResidenceExplorerLoading = (state: StoreState) => state.loading;
export const selectSort = (state: StoreState) => state.sort;

// View selectors
export const selectAllViews = (state: StoreState) => state.explorer?.views;

export const selectCurrentView = createSelector(
    [selectExplorer, selectCurrentViewName],
    (explorer, currentViewName) => explorer?.views[currentViewName]
);

// Filtered objects selector (memoized)
export const selectFilteredObjects = createSelector(
    [selectExplorerObjects, selectFilters],
    (objects, filters) => {
        if (!objects) return [];

        const filterFunction = getCurrentSite() === SITE.NO ? areaFilterNoBra : areaFilter;

        return objects
            .filter(roomFilter(filters.minRooms, filters.maxRooms))
            .filter(filterFunction(filters.minArea, filters.maxArea))
            .filter(priceFilter(filters.minPrice, filters.maxPrice))
            .filter((searchObject: SearchResult) => {
                if (filters.minPrice !== FILTERS_MIN_PRICE || filters.maxPrice !== FILTERS_MAX_PRICE) {
                    return searchObject.objectStatus !== ObjectStatus.SOLD;
                }
                return true;
            });
    }
);

// Connected objects selector (memoized)
export const selectConnectedObjects = createSelector(
    [selectExplorer, selectFilteredObjects],
    (explorer, filteredObjects) => {
        if (!explorer) return null;

        return Object.keys(explorer.apts).reduce((connectedApartments: { [key: string]: SearchResult }, aptKey: string) => {
            const apartment = explorer.apts[aptKey];
            const connectedObject = filteredObjects.find(object => object.apartmentNumber === apartment.number);

            if (connectedObject) {
                connectedApartments[aptKey.toUpperCase()] = connectedObject;
            }

            return connectedApartments;
        }, {} as { [key: string]: SearchResult });
    }
);

// View apartments selector (memoized)
export const selectViewApartments = createSelector(
    [selectExplorer],
    (explorer) => {
        if (!explorer) return null;
        return collectApartmentsByTopLevel(explorer);
    }
);

// Current clicked area selector
export const selectCurrentClickedArea = (state: StoreState) => {
    const currentView = selectCurrentView(state);
    const { selectedObjectId } = state;

    if (!currentView) return null;

    const clickArea = Object.entries(currentView.clickArea).find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- residence explorer click area with dynamic shape
        ([_key, click]) => (click as any).value.toUpperCase() === selectedObjectId
    );

    if (clickArea) {
        const [_key, area] = clickArea;
        return area;
    }

    return null;
};

// Object data from current selection
export const selectObjectDataFromCurrentSelection = (state: StoreState) => {
    const connectedObjects = selectConnectedObjects(state);
    const { selectedObjectId } = state;

    if (!connectedObjects || !selectedObjectId) {
        return null;
    }

    return connectedObjects[selectedObjectId];
};

// Sun study selectors
export const selectShowSunStudy = (state: StoreState) => state.showSunStudy;
export const selectSunStudyStarted = (state: StoreState) => state.sunStudyStarted;
export const selectSunStudyFrame = (state: StoreState) => state.sunStudyFrame;
export const selectSunStudyPeriod = (state: StoreState) => state.sunStudyPeriod;

export const selectViewHasSunStudy = (state: StoreState) => {
    const { explorer, currentViewName } = state;
    return explorer?.sunstudy?.[currentViewName] ?? null;
};

export const selectSunStudyType = (state: StoreState) => state.explorer?.explorerType;

export const selectHasSunStudy = (state: StoreState) => {
    const { explorer, settings } = state;
    return (explorer?.sunstudy && Object.keys(explorer.sunstudy).length > 0) || settings?.sunStudyExternalUrl;
};

export const selectExternalSunStudyUrl = (state: StoreState) => state.settings?.sunStudyExternalUrl;

export const selectCurrentSunStudy = (state: StoreState) => {
    const { explorer, currentViewName, sunStudyPeriod } = state;
    if (!explorer || !currentViewName || !explorer.sunstudy?.[currentViewName]) return null;
    return explorer.sunstudy[currentViewName].data[sunStudyPeriod];
};

export const selectSunStudyHasMultiplePeriods = (state: StoreState) => {
    const { explorer, currentViewName } = state;
    if (!explorer || !currentViewName || !explorer.sunstudy?.[currentViewName]) return false;
    return explorer.sunstudy[currentViewName].data.length > 1;
};

export const selectSunStudyPeriodNames = createSelector(
    [selectExplorer, selectCurrentViewName],
    (explorer, currentViewName) => {
        if (!explorer || !currentViewName || !explorer.sunstudy?.[currentViewName]) return [];
        return explorer.sunstudy[currentViewName].data.map((period) => period.name);
    }
);

// Current apartment selector (memoized)
export const selectCurrentApartment = createSelector(
    [selectExplorer, selectSetup],
    (explorer, settings) => {
        if (!explorer || !settings?.currentObjectId) return null;
        return explorer.apts[settings.currentObjectId];
    }
);

// Balcony view selector (memoized)
export const selectBalconyView = createSelector(
    [selectExplorer, selectSetup, selectCurrentApartment],
    (explorer, settings, apartment) => {
        if (!explorer?.settings || !apartment || !settings) return [];

        const tempBalconyView: string[] = [];

        if (apartment.files?.balconyViews?.length > 0) {
            apartment.files.balconyViews.forEach(x => {
                if (x.indexOf('.html') > -1) {
                    tempBalconyView.push(`${explorer.settings.projectDataUrl}${x}`);
                } else {
                    tempBalconyView.push(`/externalfiles/image/residenceexplorer/${settings.stageId}/balconyview/${apartment.number}${x}`);
                }
            });
        }

        return tempBalconyView;
    }
);

// 3D view selector (memoized)
export const selectObject3DView = createSelector(
    [selectExplorer, selectSetup, selectCurrentApartment],
    (explorer, settings, apartment) => {
        if (!explorer?.settings || !apartment || !settings) return [];

        const tempObject3DView: string[] = [];

        if (apartment.files?.object3DViews?.length > 0) {
            apartment.files.object3DViews.forEach(x => {
                if (x.url.indexOf('https:') > -1) {
                    tempObject3DView.push(x.url);
                } else {
                    tempObject3DView.push(`${explorer.settings.projectDataUrl}${x.url}`);
                }
            });
        }

        return tempObject3DView;
    }
);

// Custom hooks for memoized selectors
// These use createSelector for memoization to prevent infinite re-render loops

export const useFilteredObjects = () => {
    return useResidenceExplorerStore(selectFilteredObjects);
};

export const useConnectedObjects = () => {
    return useResidenceExplorerStore(selectConnectedObjects);
};

export const useViewApartments = () => {
    return useResidenceExplorerStore(selectViewApartments);
};

export const useBalconyView = () => {
    return useResidenceExplorerStore(selectBalconyView);
};

export const useObject3DView = () => {
    return useResidenceExplorerStore(selectObject3DView);
};

export const useSunStudyPeriodNames = () => {
    return useResidenceExplorerStore(selectSunStudyPeriodNames);
};
